import React, { useEffect, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

const PAY_NOW_GUIDELINES = [
    {
        title: "Choose Membership Amount",
        description:
            "Click the Membership button below. On the Razorpay page, enter the amount according to the membership plan you want."
    },
    {
        title: "Fill Your Details",
        description:
            "Provide your name, email address, mobile number, city and pincode."
    },
    {
        title: "Select Payment Method",
        description:
            "Choose UPI, Debit Card, Credit Card, Net Banking or Wallet."
    },
    {
        title: "Complete Payment",
        description:
            "Confirm your payment securely through Razorpay and receive your membership confirmation."
    }
];

const AUTO_MEMBERSHIP_GUIDELINES = [
    {
        title: "Monthly Membership",
        description:
            "This subscription is currently configured for recurring monthly membership plans (₹25, ₹51, or ₹101)."
    },
    {
        title: "Choose Your Plan",
        description:
            "Select your preferred membership plan from the options below."
    },
    {
        title: "Complete Registration",
        description:
            "Fill in your personal details and choose your preferred recurring payment method."
    },
    {
        title: "Approve the Mandate",
        description:
            "Authenticate using OTP or your bank's authorization process to activate the recurring mandate."
    },
    {
        title: "Automatic Monthly Membership",
        description:
            "Your selected amount will be deducted automatically every month until you cancel the subscription."
    }
];

const MEMBERSHIP_PLANS = [
  {
    tier: '25',
    amount: 25,
   },
  {
    tier: '51',
    amount: 51,
  },
  {
    tier: '101',
    amount: 101,
  }
];

const SCAN_QR_GUIDELINES = [
    {
        title: "Click Show QR Code",
        description:
            "Click the Show QR Code button below to reveal the QR code for scanning it."
    },
    {
        title: "Open Your UPI App",
        description:
            "Open any UPI app (Google Pay, PhonePe, Paytm, BHIM, or your bank app)."
    },
    {
        title: "Scan the QR Code",
        description:
            "Tap the Scan QR option and point your camera at the QR code shown."
    },
    {
        title: "Complete Payment",
        description:
            "Confirm the payment with your UPI PIN. Your membership activates instantly."
    }
];

export default function HostedMembership() {
    const paymentContainerRef = useRef(null);
    const qrContainerRef = useRef(null);

    const [activeTab, setActiveTab] = useState("paynow");
    const [showQr, setShowQr] = useState(false);
    const [qrLoaded, setQrLoaded] = useState(false);

    // Auto-Membership form states
    const [memberName, setMemberName] = useState("");
    const [memberEmail, setMemberEmail] = useState("");
    const [memberPhone, setMemberPhone] = useState("");
    const [memberLoading, setMemberLoading] = useState(false);
    const [memberError, setMemberError] = useState("");
    const [selectedTier, setSelectedTier] = useState('25'); // Default to ₹25 plan

    const paymentButtonId = process.env.REACT_APP_PAYMENT_BUTTON_ID || "pl_TB9iQiGxbmTpWD";

    useEffect(() => {
        console.log("Payment Button ID:", paymentButtonId);
    }, []);

    useEffect(() => {
        if (paymentContainerRef.current?.children.length) return;
        if (!paymentButtonId) return;

        const container = paymentContainerRef.current;
        if (!container) return;

        container.innerHTML = "";
        const form = document.createElement("form");
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/payment-button.js";
        script.async = true;
        script.setAttribute("data-payment_button_id", paymentButtonId);
        form.appendChild(script);
        container.appendChild(form);
    }, [paymentButtonId]);

    const handleAutoMembershipSubmit = async (e) => {
        e.preventDefault();
        setMemberError("");

        if (!memberName || !memberEmail || !memberPhone) {
            setMemberError("Please fill in all your details.");
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(memberEmail)) {
            setMemberError("Please enter a valid email address.");
            return;
        }

        // Basic phone validation (10 digits)
        const phoneDigits = memberPhone.replace(/\D/g, "");
        if (phoneDigits.length < 10) {
            setMemberError("Please enter a valid 10-digit phone number.");
            return;
        }

        setMemberLoading(true);

        try {
            const response = await fetch(`${API_BASE}/payment/subscription`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    donorName: memberName,
                    donorEmail: memberEmail,
                    donorPhone: phoneDigits,
                    tier: selectedTier
                })
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.detail || "Failed to create subscription. Please try again.");
            }

            // Open the Razorpay checkout URL in a new tab
            if (data.shortUrl) {
                window.open(data.shortUrl, "_blank", "noopener,noreferrer");
            } else {
                throw new Error("No checkout URL received from server.");
            }
        } catch (err) {
            console.error("Auto-Membership error:", err);
            setMemberError(err.message || "Something went wrong. Please try again.");
        } finally {
            setMemberLoading(false);
        }
    };

    const handleShowQr = () => {
        setShowQr(true);
        setQrLoaded(false);
        // Scroll to QR container after state update
        setTimeout(() => {
            qrContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const getGuidelines = () => {
        switch (activeTab) {
            case "paynow": return PAY_NOW_GUIDELINES;
            case "automember": return AUTO_MEMBERSHIP_GUIDELINES;
            case "scanqr": return SCAN_QR_GUIDELINES;
            default: return PAY_NOW_GUIDELINES;
        }
    };

    const getGuidelineTitle = () => {
        switch (activeTab) {
            case "paynow": return "Join Now — Steps";
            case "automember": return "Auto-Membership — Steps";
            case "scanqr": return "Scan QR — Steps";
            default: return "Join Now — Steps";
        }
    };

    return (
        <div className="card p-6">
            <div className="text-center mb-6">
                <h3 className="font-display text-2xl text-amber-900 mb-3">
                    Join Membership Securely
                </h3>
                <p className="text-amber-700 leading-relaxed">
                    Complete your membership securely through Razorpay.
                    We support UPI, Cards, Net Banking and Wallets.
                </p>
            </div>
            <div className="flex justify-center gap-3 mb-8 flex-wrap">
                <button
                    type="button"
                    onClick={() => { setActiveTab("paynow"); setShowQr(false); }}
                    className={`px-5 py-2 rounded text-sm font-medium transition-colors ${
                        activeTab === "paynow"
                            ? "bg-orange-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                    Join Now
                </button>
                <button
                    type="button"
                    onClick={() => { setActiveTab("automember"); setShowQr(false); }}
                    className={`px-5 py-2 rounded text-sm font-medium transition-colors ${
                        activeTab === "automember"
                            ? "bg-orange-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                    Auto-Membership
                </button>
                <button
                    type="button"
                    onClick={() => { setActiveTab("scanqr"); setShowQr(false); }}
                    className={`px-5 py-2 rounded text-sm font-medium transition-colors ${
                        activeTab === "scanqr"
                            ? "bg-orange-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                    Scan QR
                </button>
            </div>

            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
                <h4 className="font-display text-lg text-amber-900 mb-5 text-center">
                    {getGuidelineTitle()}
                </h4>
                <div className="space-y-5">
                    {getGuidelines().map((item, index) => (
                        <div key={index} className="flex gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center font-semibold text-sm">
                                {index + 1}
                            </div>
                            <div>
                                <h5 className="font-semibold text-amber-900">{item.title}</h5>
                                <p className="text-sm text-amber-700 mt-1">{item.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Join Now Tab */}
            <div
                style={{
                    display: activeTab === "paynow" ? "flex" : "none",
                    justifyContent: "center"
                }}
            >
                <div ref={paymentContainerRef} className="min-h-[70px]"/>
            </div>

            {/* Auto-Membership Tab */}
            <div style={{ display: activeTab === "automember" ? "block" : "none" }}>
                {/* Plan Selection */}
                <div className="mb-6">
                    <h4 className="font-display text-lg text-amber-900 mb-4 text-center">Choose Your Membership Plan</h4>
                    <p className="text-sm text-amber-700 mb-4 text-center">Select a monthly membership plan that suits your support level.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {MEMBERSHIP_PLANS.map((plan) => (
                            <button
                                key={plan.tier}
                                type="button"
                                onClick={() => setSelectedTier(plan.tier)}
                                className={`relative p-4 rounded-lg border-2 transition-all text-left ${
                                    selectedTier === plan.tier
                                        ? 'border-orange-600 bg-orange-50 shadow-lg'
                                        : 'border-amber-200 bg-white hover:border-orange-300 hover:bg-amber-50'
                                }`}
                            >
                                <div className="flex items-baseline gap-1 mb-2">
                                    <span className="font-display text-2xl font-bold text-amber-900">₹{plan.amount}</span>
                                    <span className="text-amber-600 text-sm">/month</span>
                                </div>
                                {/* <h5 className="font-semibold text-amber-900 mb-1">{plan.label}</h5> */}
                                {selectedTier === plan.tier && (
                                    <div className="absolute inset-0 border-2 border-orange-600 rounded-lg pointer-events-none" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <form onSubmit={handleAutoMembershipSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-amber-700 mb-1">Full Name *</label>
                        <input
                            type="text"
                            value={memberName}
                            onChange={(e) => setMemberName(e.target.value)}
                            className="w-full border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Enter your full name"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-amber-700 mb-1">Email Address *</label>
                        <input
                            type="email"
                            value={memberEmail}
                            onChange={(e) => setMemberEmail(e.target.value)}
                            className="w-full border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Enter your email"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-amber-700 mb-1">Phone Number *</label>
                        <input
                            type="tel"
                            value={memberPhone}
                            onChange={(e) => setMemberPhone(e.target.value)}
                            className="w-full border border-amber-200 rounded-sm px-4 py-2 text-amber-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            placeholder="Enter your 10-digit mobile number"
                            required
                        />
                    </div>

                    {memberError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-700">
                            {memberError}
                        </div>
                    )}

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={memberLoading}
                            className="w-full inline-flex items-center justify-center px-8 py-3 bg-orange-600 text-white font-semibold rounded hover:bg-orange-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {memberLoading ? (
                                <>
                                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                    Creating your membership...
                                </>
                            ) : (
                                "Set Up Monthly Membership"
                            )}
                        </button>
                    </div>

                    <p className="text-center text-xs text-amber-600">
                        Opens Razorpay in a new tab for secure e-mandate setup.
                        Your bank details are entered directly on Razorpay's secure page.
                    </p>
                </form>
            </div>

            {/* Scan QR Tab */}
            <div style={{ display: activeTab === "scanqr" ? "block" : "none" }}>
                <div className="flex flex-col items-center gap-4 min-h-[70px]">
                    {!showQr ? (
                        <button
                            type="button"
                            onClick={handleShowQr}
                            className="inline-flex items-center justify-center px-8 py-3 bg-orange-600 text-white font-semibold rounded hover:bg-orange-700 transition-colors"
                        >
                            Show QR Code
                        </button>
                    ) : (
                        <div
                            ref={qrContainerRef}
                            className="w-full max-w-sm animate-slide-down"
                        >
                            <div className="bg-white rounded-xl shadow-lg p-6 border border-amber-200 relative">
                                <button
                                    type="button"
                                    onClick={() => setShowQr(false)}
                                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center hover:bg-amber-200 transition-colors"
                                    aria-label="Close"
                                >
                                    ✕
                                </button>
                                <h4 className="font-display text-lg text-amber-900 text-center mb-4">
                                    Scan to Join Membership
                                </h4>
                                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                                    <img
                                        src="/qr_code/QrCode.jpeg"
                                        alt="BSM Bhojan Seva Mandal QR Code for UPI Membership"
                                        className="w-full h-auto object-contain"
                                        onLoad={() => setQrLoaded(true)}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div class="text-amber-700 text-sm p-4 text-center">QR Code not available. Please use another payment method.</div>';
                                        }}
                                    />
                                </div>
                                <p className="text-center text-sm text-amber-600 mt-4">
                                    Open any UPI app and scan to join membership
                                </p>
                            </div>
                        </div>
                    )}
                    <p className="text-center text-sm text-amber-600">
                        Click to reveal the QR code for UPI membership.
                    </p>
                </div>
                <p className="text-center text-xs text-amber-600 mt-4">
                    Any UPI app can scan it — Google Pay, PhonePe, Paytm, BHIM, etc.
                </p>
            </div>

            <p className="text-center text-xs text-amber-500 mt-6">
                🔒 Membership payments are securely processed by Razorpay.
            </p>
        </div>
    );
}